import { inject } from "../../di";
import type { IRpcService } from "../rpc/types";
import type {
  IWritableLiveEventFormState,
  ILiveEventFormService,
  LiveEventFormData,
  AvailableLink,
  StreamSource,
} from "./types";
import type { GetLiveEventByIdResult } from "api-contracts/rpc/get-live-event-by-id";
import type { CreateLiveEventResult } from "api-contracts/rpc/create-live-event";
import type { UpdateLiveEventResult } from "api-contracts/rpc/update-live-event";
import { createLiveEventValidators } from "../../form/validators/live-event-validators";
import { NotificationService } from "../../../ui-components/notification";
import { queryClient } from "../../data-query-controller";

export class LiveEventFormService implements ILiveEventFormService {
  @inject<IWritableLiveEventFormState>("writableLiveEventFormState")
  private state!: IWritableLiveEventFormState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  private translateFn: (key: string, params?: Record<string, unknown>) => string = (key: string) => key;
  private pendingLoadEvent: Promise<void> | undefined;
  private pendingLoadEventId: string | undefined;
  private linksLoaded = false;

  private get validators() {
    return createLiveEventValidators(this.translateFn);
  }

  setTranslationFunction(translateFn: (key: string, params?: Record<string, unknown>) => string): void {
    this.translateFn = translateFn;
  }

  initialize(): void {
    this.loadAvailableLinks();
  }

  initializeNewEvent(): void {
    if (!this.state.eventId.get() && !this.state.provisionalEventId.get()) {
      this.state.provisionalEventId.set(crypto.randomUUID());
    }
  }

  async loadEvent(id?: string): Promise<void> {
    if (!id) {
      this.state.eventId.set(undefined);
      return;
    }

    if (this.pendingLoadEventId === id && this.pendingLoadEvent) {
      return this.pendingLoadEvent;
    }

    this.pendingLoadEventId = id;
    this.pendingLoadEvent = this.doLoadEvent(id);

    try {
      await this.pendingLoadEvent;
    } finally {
      this.pendingLoadEvent = undefined;
      this.pendingLoadEventId = undefined;
    }
  }

  private async doLoadEvent(id: string): Promise<void> {
    try {
      this.state.isLoading.set(true);
      this.state.eventId.set(id);

      const result = (await this.rpcService.call("get-live-event-by-id", { event_id: id })) as GetLiveEventByIdResult;

      if (!result.event) {
        console.error("Event not found");
        NotificationService.error("Event not found");
        return;
      }

      const event = result.event;

      // Update form data
      const scheduledDate = event.scheduled_start_time
        ? (() => {
            const d = new Date(event.scheduled_start_time);
            return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
          })()
        : "";

      const scheduledTime = event.scheduled_start_time
        ? (() => {
            const d = new Date(event.scheduled_start_time);
            return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
          })()
        : "";

      const durationMinutes = event.expected_duration ? event.expected_duration.toString() : "";

      this.state.formData.set({
        title: event.title || "",
        description: event.description || "",
        feeEuros: event.fee_cents ? (event.fee_cents / 100).toFixed(2) : "",
        scheduledDate,
        scheduledTime,
        maxAttendees: event.max_attendees ? event.max_attendees.toString() : "",
        whatsappUrl: event.whatsapp_url || "",
        ctaText: event.cta_text || "",
        durationMinutes,
        language: event.language || "nl",
      });

      // Update UI toggles
      this.state.uiToggles.set({
        showDescription: !!event.description,
        showMaxAttendees: !!event.max_attendees,
        showWhatsApp: !!event.whatsapp_url,
        showCtaText: !!event.cta_text,
        showLinkedLinks: !!(event.linked_links && event.linked_links.length > 0),
      });

      // Update other state
      this.state.contentTier.set("premium");
      this.state.thumbnailFileId.set(event.thumbnail_file_id || undefined);
      this.state.thumbnailUrl.set(event.thumbnail_url || undefined);
      this.state.streamSource.set(event.stream_delivery === "obs" ? "obs" : "webcam");

      if (event.linked_links && event.linked_links.length > 0) {
        this.state.linkedLinkIds.set(event.linked_links.map((link) => link.id));
      }
    } catch (error) {
      console.error("Error loading event:", error);
      NotificationService.error("Failed to load event");
    } finally {
      this.state.isLoading.set(false);
    }
  }

  async duplicateEvent(id: string): Promise<void> {
    await this.loadEvent(id);
    // Clear eventId so saveEvent creates a new event instead of updating
    this.state.eventId.set(undefined);
    // Append "(1)" to the title
    const currentData = this.state.formData.get();
    this.state.formData.set({
      ...currentData,
      title: `${currentData.title} (1)`,
    });
  }

  async saveEvent(): Promise<boolean> {
    if (!this.validateForm()) {
      return false;
    }

    const data = this.state.formData.get();
    const contentTier = this.state.contentTier.get();
    const eventId = this.state.eventId.get();

    this.state.isSubmitting.set(true);

    try {
      const localDateTime = new Date(`${data.scheduledDate}T${data.scheduledTime}:00`);
      const isoString = localDateTime.toISOString();

      const streamDelivery = this.state.streamSource.get() === "obs" ? ("obs" as const) : ("browser" as const);

      if (eventId) {
        // Update existing event
        const updateParams = {
          event_id: eventId,
          title: data.title.trim(),
          description: data.description?.trim() || undefined,
          content_tier: contentTier,
          fee_cents: data.feeEuros ? Math.round(parseFloat(data.feeEuros) * 100) : null,
          scheduled_start_time: isoString,
          max_attendees: data.maxAttendees ? Number(data.maxAttendees) : null,
          thumbnail_file_id: this.state.thumbnailFileId.get() || null,
          whatsapp_url: data.whatsappUrl?.trim() || undefined,
          post_buying_video_file_id: null,
          cta_text: data.ctaText?.trim() || null,
          expected_duration: this.calculateDurationMinutes(data.durationMinutes),
          link_ids: this.state.uiToggles.get().showLinkedLinks ? this.state.linkedLinkIds.get() : null,
          stream_delivery: streamDelivery,
          language: data.language || undefined,
        };

        const result = (await this.rpcService.call("update-live-event", updateParams)) as UpdateLiveEventResult;
        if (result.success) {
          queryClient.removeQueries({ queryKey: ["live-events"] });
          return true;
        }
      } else {
        // Create new event
        const params = {
          event_id: this.state.provisionalEventId.get(),
          title: data.title.trim(),
          description: data.description?.trim() || undefined,
          content_tier: contentTier,
          fee_cents: data.feeEuros ? Math.round(parseFloat(data.feeEuros) * 100) : undefined,
          scheduled_start_time: isoString,
          max_attendees: data.maxAttendees ? Number(data.maxAttendees) : undefined,
          thumbnail_file_id: this.state.thumbnailFileId.get(),
          whatsapp_url: data.whatsappUrl?.trim() || undefined,
          post_buying_video_file_id: undefined,
          cta_text: data.ctaText?.trim() || undefined,
          expected_duration: this.calculateDurationMinutes(data.durationMinutes),
          link_ids:
            this.state.uiToggles.get().showLinkedLinks && this.state.linkedLinkIds.get().length > 0
              ? this.state.linkedLinkIds.get()
              : undefined,
          stream_delivery: streamDelivery,
          language: data.language || undefined,
        };

        const result = (await this.rpcService.call("create-live-event", params)) as CreateLiveEventResult;
        if (result.success) {
          queryClient.removeQueries({ queryKey: ["live-events"] });
          const createdEvent = result.event;
          if (createdEvent) {
            await this.loadEvent(createdEvent.id);
          }
          return true;
        }
      }

      NotificationService.error("Failed to save event");
      return false;
    } catch (error) {
      console.error("Failed to save live event:", error);
      NotificationService.error("Failed to save event. Please try again.");
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  updateFormData(field: keyof LiveEventFormData, value: string): void {
    const currentData = this.state.formData.get();
    this.state.formData.set({
      ...currentData,
      [field]: value,
    });

    this.clearFieldErrorIfValid(field, value);
  }

  validateFieldOnBlur(field: keyof LiveEventFormData): void {
    const data = this.state.formData.get();
    const value = data[field];
    const error = this.validateSingleField(field, value);
    const currentErrors = { ...this.state.validationErrors.get() };

    if (error) {
      currentErrors[field] = error;
    } else {
      delete currentErrors[field];
    }

    if (field === "scheduledDate" || field === "scheduledTime") {
      const timeFieldError = this.validateSingleField("scheduledTime", data.scheduledTime);
      if (timeFieldError) {
        currentErrors.scheduledTime = timeFieldError;
      } else {
        const dateTimeError = this.validators.validateScheduledDateTime(data.scheduledDate, data.scheduledTime);
        if (dateTimeError) {
          currentErrors.scheduledTime = dateTimeError;
        } else {
          delete currentErrors.scheduledTime;
        }
      }
    }

    this.state.validationErrors.set(currentErrors);
  }

  private clearFieldErrorIfValid(field: keyof LiveEventFormData, value: string): void {
    const currentErrors = this.state.validationErrors.get();
    if (!currentErrors[field]) return;

    const error = this.validateSingleField(field, value);
    if (!error) {
      const newErrors = { ...currentErrors };
      delete newErrors[field];

      if ((field === "scheduledDate" || field === "scheduledTime") && newErrors.scheduledTime) {
        const data = this.state.formData.get();
        const updatedData = { ...data, [field]: value };
        const dateTimeError = this.validators.validateScheduledDateTime(
          updatedData.scheduledDate,
          updatedData.scheduledTime,
        );
        if (!dateTimeError) {
          delete newErrors.scheduledTime;
        }
      }

      this.state.validationErrors.set(newErrors);
    }
  }

  private validateSingleField(field: keyof LiveEventFormData, value: string): string | undefined {
    switch (field) {
      case "title":
        return this.validators.validateTitle(value);
      case "description":
        return this.validators.validateDescription(value);
      case "feeEuros":
        return this.validators.createFeeValidator(() => this.state.contentTier.get())(value);
      case "scheduledDate":
        return this.validators.validateDate(value);
      case "scheduledTime":
        return this.validators.validateTime(value);
      case "maxAttendees":
        return value ? this.validators.validateMaxAttendees(value) : undefined;
      case "durationMinutes":
        return this.validators.validateDuration(value);
      default:
        return undefined;
    }
  }

  updateUIToggle(toggle: keyof import("./types").UIToggleState, value: boolean): void {
    const currentToggles = this.state.uiToggles.get();
    this.state.uiToggles.set({
      ...currentToggles,
      [toggle]: value,
    });
  }

  setThumbnailData(fileId: string | undefined, url?: string): void {
    this.state.thumbnailFileId.set(fileId);
    if (url) {
      this.state.thumbnailUrl.set(url);
    }
    if (fileId) {
      const currentErrors = { ...this.state.validationErrors.get() };
      delete currentErrors.thumbnail;
      this.state.validationErrors.set(currentErrors);
    }
  }

  toggleLinkedLink(linkId: string, checked: boolean): void {
    const currentLinks = this.state.linkedLinkIds.get();
    if (checked) {
      this.state.linkedLinkIds.set([...currentLinks, linkId]);
    } else {
      this.state.linkedLinkIds.set(currentLinks.filter((id) => id !== linkId));
    }
  }

  validateForm(): boolean {
    const data = this.state.formData.get();
    const contentTier = this.state.contentTier.get();
    const errors: Record<string, string> = {};

    // Title validation
    const titleResult = this.validators.validateTitle(data.title);
    if (titleResult) errors.title = titleResult;

    // Description validation
    const descriptionResult = this.validators.validateDescription(data.description);
    if (descriptionResult) errors.description = descriptionResult;

    // Thumbnail validation
    const thumbnailResult = this.validators.validateThumbnail(this.state.thumbnailFileId.get());
    if (thumbnailResult) errors.thumbnail = thumbnailResult;

    // Fee validation for premium events
    const feeResult = this.validators.createFeeValidator(() => contentTier)(data.feeEuros);
    if (feeResult) errors.feeEuros = feeResult;

    // Date validation
    const dateResult = this.validators.validateDate(data.scheduledDate);
    if (dateResult) errors.scheduledDate = dateResult;

    // Time validation
    const timeResult = this.validators.validateTime(data.scheduledTime);
    if (timeResult) errors.scheduledTime = timeResult;

    // Date+time combined validation (must be in the future)
    if (!errors.scheduledDate && !errors.scheduledTime) {
      const dateTimeResult = this.validators.validateScheduledDateTime(data.scheduledDate, data.scheduledTime);
      if (dateTimeResult) errors.scheduledTime = dateTimeResult;
    }

    // Max attendees validation
    if (data.maxAttendees) {
      const maxAttendeesResult = this.validators.validateMaxAttendees(data.maxAttendees);
      if (maxAttendeesResult) errors.maxAttendees = maxAttendeesResult;
    }

    this.state.validationErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  validateInfoTab(): boolean {
    const data = this.state.formData.get();
    const contentTier = this.state.contentTier.get();
    const errors: Record<string, string> = {};

    // Fee validation for premium events
    const feeResult = this.validators.createFeeValidator(() => contentTier)(data.feeEuros);
    if (feeResult) errors.feeEuros = feeResult;

    // Date validation
    const dateResult = this.validators.validateDate(data.scheduledDate);
    if (dateResult) errors.scheduledDate = dateResult;

    // Time validation
    const timeResult = this.validators.validateTime(data.scheduledTime);
    if (timeResult) errors.scheduledTime = timeResult;

    // Date+time combined validation (must be in the future)
    if (!errors.scheduledDate && !errors.scheduledTime) {
      const dateTimeResult = this.validators.validateScheduledDateTime(data.scheduledDate, data.scheduledTime);
      if (dateTimeResult) errors.scheduledTime = dateTimeResult;
    }

    // Max attendees validation
    if (data.maxAttendees) {
      const maxAttendeesResult = this.validators.validateMaxAttendees(data.maxAttendees);
      if (maxAttendeesResult) errors.maxAttendees = maxAttendeesResult;
    }

    this.state.validationErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  setStreamSource(source: StreamSource): void {
    this.state.streamSource.set(source);
  }

  reset(): void {
    this.state.reset();
  }

  private async loadAvailableLinks(): Promise<void> {
    if (this.linksLoaded) return;

    try {
      this.state.isLoadingLinks.set(true);
      const result = (await this.rpcService.call("getUnifiedLinks", {})) as { links?: AvailableLink[] };
      if (result && result.links) {
        const links = result.links
          .filter((link: { status?: "live" | "upcoming" | "expired" }) => link.status !== "expired")
          .map((link: AvailableLink) => ({
            id: link.id,
            title: link.title,
            has_discount: link.has_discount,
            code: link.code,
          }));
        this.state.availableLinks.set(links);
      }
      this.linksLoaded = true;
    } catch (error) {
      console.error("Error loading available links:", error);
      NotificationService.error("Failed to load available links");
    } finally {
      this.state.isLoadingLinks.set(false);
    }
  }

  private calculateDurationMinutes(minutes: string): number | undefined {
    if (!minutes) return undefined;
    const minutesNum = Number(minutes);
    return minutesNum > 0 ? minutesNum : undefined;
  }
}
